const ALLOWED_CARD_TYPES = [1, 3];
const MANAGER_ROLES = ["admin", "izbank-admin"];

function toInteger(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  return parseInt(String(value || "0").replace(/,/g, ""), 10) || 0;
}

function buildBranchOrderItems(items) {
  return items.map((item) => {
    const cardType = toInteger(item.cardType);
    if (!ALLOWED_CARD_TYPES.includes(cardType)) {
      throw new Error(`Unsupported cardType: ${cardType}`);
    }

    const mappedItem = {
      themeId: toInteger(item.themeId),
      cardType,
      itemsCount: toInteger(item.itemsCount),
      depositId: toInteger(item.depositId),
      optionalCredit: toInteger(item.optionalCredit),
      mediaId: toInteger(item.mediaId),
      showAmountOnCard: Boolean(item.showAmountOnCard),
    };

    if (Array.isArray(item.metaDatas) && item.metaDatas.length) {
      mappedItem.metaDatas = item.metaDatas;
    }

    return mappedItem;
  });
}

function calculateChargeTotal(items) {
  return items.reduce((total, item) => {
    const amount =
      item.deposit !== undefined
        ? toInteger(item.deposit)
        : item.depositId == 61 || item.depositId == 102
        ? toInteger(item.optionalCredit)
        : toInteger(item.depositValue);
    return total + amount * toInteger(item.itemsCount);
  }, 0);
}

function hasValue(value) {
  return value !== undefined && value !== null && String(value).trim() !== "";
}

function formatBranchOptionLabel(branch = {}) {
  const name = branch.brancheName || branch.branchName;
  const code = branch.brancheCode || branch.branchCode;
  return [name, code].filter(hasValue).join(" - ");
}

function isManagerRole(role) {
  return MANAGER_ROLES.includes(role);
}

function isBranchRole(role) {
  return role === "branches";
}

function assertSupportedOrderRole(role) {
  if (!isManagerRole(role) && !isBranchRole(role)) {
    throw new Error(`Unsupported role: ${role}`);
  }
}

function normalizeCustomerType(customerType) {
  const normalizedType = toInteger(customerType);
  if (normalizedType === 0 || normalizedType === 1) return 1;
  if (normalizedType === 2) return 2;
  throw new Error(`Unsupported customerType: ${customerType}`);
}

function addManagerBranchCode(payload, role, branchCode) {
  assertSupportedOrderRole(role);
  if (isManagerRole(role)) {
    if (!hasValue(branchCode)) throw new Error("branchCode is required");
    payload.branchCode = branchCode;
  }
  return payload;
}

function buildCustomerFindPayload({
  role,
  branchCode,
  customerType,
  identifier,
}) {
  if (!hasValue(identifier)) throw new Error("customer identifier is required");

  const normalizedType = normalizeCustomerType(customerType);
  const payload =
    normalizedType === 2
      ? { nationalId: String(identifier) }
      : { nationalCode: String(identifier) };

  return addManagerBranchCode(payload, role, branchCode);
}

function buildCustomerRegistrationPayload({
  role,
  branchCode,
  customerType,
  nationalCode,
  birthDate,
  mobile,
  companyName,
  nationalId,
  registerNumber,
}) {
  const normalizedType = normalizeCustomerType(customerType);
  if (!hasValue(nationalCode)) throw new Error("nationalCode is required");
  if (!hasValue(birthDate)) throw new Error("birthDate is required");
  if (!hasValue(mobile)) throw new Error("mobile is required");

  const payload = {
    customerType: normalizedType,
    nationalCode: String(nationalCode),
    birthDate: String(birthDate),
    mobile: String(mobile),
  };

  if (normalizedType === 2) {
    if (!hasValue(companyName)) throw new Error("companyName is required");
    if (!hasValue(nationalId)) throw new Error("nationalId is required");
    payload.companyName = companyName;
    payload.nationalId = String(nationalId);
    if (hasValue(registerNumber)) {
      payload.registerNumber = String(registerNumber);
    }
  }

  return addManagerBranchCode(payload, role, branchCode);
}

function canUseBranchCustomerActions(role) {
  return role === "branches" || role === "izbank-admin";
}

function canUsePublicRelationsActions(role) {
  return role === "bankpublicrelations" || role === "izbank-admin";
}

function canUseBranchPaymentActions(role) {
  return isManagerRole(role) || isBranchRole(role);
}

function canUseBranchDesignActions(role) {
  return ["branches", "admin", "bankpublicrelations", "izbank-admin"].includes(role);
}

function canUseBranchFinanceActions(role) {
  return role === "admin" || role === "finance";
}

function canUseBranchStatusActions(role) {
  return ["branches", "admin", "bankpublicrelations", "organization", "izbank-admin"].includes(role);
}

function canExportBranchCardSheet(order = {}, role) {
  const firstItem = Array.isArray(order.orderItems) && order.orderItems[0];
  return role === "admin" && order.orderTypeId != 2 && Boolean(firstItem && firstItem.cardId && firstItem.getTrack);
}

function requiresCustomerForOrder(role, orderTarget) {
  if (isBranchRole(role)) return true;
  return isManagerRole(role) && orderTarget === "customer";
}

function canCreateBranchOrder({ role, branchCode, orderTarget, customerId }) {
  if (isManagerRole(role)) {
    if (!hasValue(branchCode)) return false;
    if (orderTarget === "branch") return true;
    return orderTarget === "customer" && toInteger(customerId) > 0;
  }
  return isBranchRole(role) && toInteger(customerId) > 0;
}

function hasRequiredPaymentData(chargeTotal, payment = {}) {
  if (toInteger(chargeTotal) <= 0) return true;
  return hasValue(payment.base64Receipt) &&
    (hasValue(payment.rrn) || hasValue(payment.paymentIdentifier));
}

function getBranchRegistrationOutcome(response = {}) {
  const customerId = toInteger(response.result && response.result.customerId);
  if (response.status === true && customerId > 0) {
    return {
      kind: "selected",
      customerId,
      alreadyExists: response.result.alreadyExists === true,
    };
  }

  return { kind: "error" };
}

function getBranchCustomerFindOutcome(response = {}) {
  if (response.status !== true) return { kind: "error" };
  if (!Object.prototype.hasOwnProperty.call(response, "result")) {
    return response.responseCode === "00" ? { kind: "notFound" } : { kind: "error" };
  }
  const customerId = toInteger(response.result && response.result.customerId);
  if (customerId > 0) {
    return { kind: "found", customerId, customer: response.result };
  }
  return { kind: "error" };
}

function getBranchErrorMessage(response = {}) {
  const messages = Array.isArray(response.errors)
    ? response.errors.map((error) => error && error.message).filter(Boolean)
    : [];
  return messages.join("\n") || response.description || "درخواست انجام نشد. لطفاً دوباره تلاش کنید.";
}

function getBranchOrderDisplayFields(order = {}) {
  const requester = order.requester || {};
  const requesterName = [requester.firstName, requester.lastName]
    .filter(Boolean)
    .join(" ");
  const metadata = order.domainMetadata || {};

  return {
    registeredBy: order.registeredBy || requesterName,
    mobile: order.mobile || order.customerMobile || metadata.mobile || "",
  };
}

function canResubmitBranchPayment(statusId, payment = {}) {
  return toInteger(statusId) === 368 && hasRequiredPaymentData(1, payment);
}

function buildBranchOrderPayload({
  role,
  branchCode,
  orderTarget,
  customerId,
  basket,
  rrn,
  paymentIdentifier,
  base64Receipt,
}) {
  const payload = { items: buildBranchOrderItems(basket) };

  assertSupportedOrderRole(role);
  if (isManagerRole(role)) {
    addManagerBranchCode(payload, role, branchCode);
    if (orderTarget !== "branch" && orderTarget !== "customer") {
      throw new Error("orderTarget is required");
    }
    if (orderTarget === "customer") {
      if (!toInteger(customerId)) throw new Error("customerId is required");
      payload.customerId = toInteger(customerId);
    }
  } else {
    if (!toInteger(customerId)) throw new Error("customerId is required");
    payload.customerId = toInteger(customerId);
  }

  const chargeTotal = calculateChargeTotal(basket);
  if (chargeTotal > 0) {
    if (
      !hasRequiredPaymentData(chargeTotal, {
        rrn,
        paymentIdentifier,
        base64Receipt,
      })
    ) {
      throw new Error("payment data is required");
    }
    if (hasValue(rrn)) payload.rrn = rrn;
    if (hasValue(paymentIdentifier)) payload.paymentIdentifier = paymentIdentifier;
    payload.base64Receipt = base64Receipt;
  }

  return payload;
}

module.exports = {
  ALLOWED_CARD_TYPES,
  buildBranchOrderItems,
  buildBranchOrderPayload,
  buildCustomerFindPayload,
  buildCustomerRegistrationPayload,
  canCreateBranchOrder,
  requiresCustomerForOrder,
  canUseBranchCustomerActions,
  canUseBranchPaymentActions,
  canUseBranchDesignActions,
  canUseBranchFinanceActions,
  canUseBranchStatusActions,
  canExportBranchCardSheet,
  canUsePublicRelationsActions,
  canResubmitBranchPayment,
  calculateChargeTotal,
  formatBranchOptionLabel,
  getBranchRegistrationOutcome,
  getBranchCustomerFindOutcome,
  getBranchErrorMessage,
  getBranchOrderDisplayFields,
  hasRequiredPaymentData,
  isManagerRole,
};
